import { Link } from 'react-router-dom';
import { DocSection, kbd } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'analyzed', label: 'Is the footage analyzed?' },
  { id: 'included', label: 'Is it included in search?' },
  { id: 'license', label: 'Is your license active / trial used up?' },
  { id: 'nothing-back', label: 'Nothing comes back' },
];

export default function Body() {
  return (
    <>
      <DocSection id="analyzed" title="Is the footage analyzed?">
        <p>
          Search only returns scenes ClipRadar has actually analyzed — an unanalyzed file returns
          nothing, silently, with no error to point at.
        </p>
        <p>
          Open <Link to="/docs/interface/media#media-table">Media</Link> and check the VISUAL and
          SPEECH columns for the file: a green check means that pass is done, a dashed box with a{' '}
          <span style={kbd}>+</span> means it's eligible but hasn't been queued yet, and a blue
          check means it's queued and running. A <strong>Visuals</strong> search needs the Visual
          pass; a <strong>Speech</strong> search needs the Speech pass. Click the box to queue
          whichever one is missing. See{' '}
          <Link to="/docs/concepts/analyzing-media">Analyzing your footage</Link> for what each
          pass covers.
        </p>
      </DocSection>

      <DocSection id="included" title="Is it included in search?">
        <p>
          Even a fully analyzed file can be excluded from search on purpose — check the SEARCH
          column's dot before assuming something's broken.
        </p>
        <p>
          A filled blue magnifier means the file is included; a hollow ring means it's excluded.
          Click the dot to toggle it.
        </p>
        <DocShot
          slug="cant-search"
          shot="include-dot"
          alt="The SEARCH column's include-in-search dot in the media table"
        />
      </DocSection>

      <DocSection id="license" title="Is your license active / trial used up?">
        <p>
          If search stops and shows a license-required dialog, or your quota chip reads 0, that's
          ClipRadar blocking search on purpose — not an error.
        </p>
        <p>
          A free trial includes 40 searches; the quota chip ("N / M left") turns amber at 5 left
          and red at 0. When the backend blocks a search it returns HTTP 402, which the app turns
          into the license-required dialog with a way to activate a key or upgrade — never a raw
          error screen.
        </p>
        <DocShot
          slug="cant-search"
          shot="license-dialog"
          alt="The license-required dialog shown when search is blocked"
        />
        <p>
          See <Link to="/docs/concepts/licensing#blocked-search">Licensing & activation</Link> for
          what each blocked state means and how to clear it.
        </p>
      </DocSection>

      <DocSection id="nothing-back" title="Nothing comes back">
        <p>
          Zero results almost always means the query or the scope is narrower than the footage —
          not that ClipRadar missed something.
        </p>
        <ul>
          <li>Check the Scope filter isn't limited to a collection, folder, or selection that doesn't contain the moment.</li>
          <li>Try the other search mode — a phrase that's easy to describe visually may be hard to say out loud, and vice versa.</li>
          <li>Under Ultra, check the chips actually captured what you meant; edit or remove a chip and re-run.</li>
        </ul>
        <DocShot
          slug="cant-search"
          shot="empty-results"
          alt="The 0-results state under a search block"
        />
        <p>
          For query and scope tactics in depth, see{' '}
          <Link to="/docs/guides/search-best-practices">Get better search results</Link>.
        </p>
      </DocSection>
    </>
  );
}
